import { useCallback, useState } from 'react';

/**
 * Field errors for a form: checked as the person leaves a field (blur), cleared as they
 * type, and the server's `fields` map put back on the right field.
 *
 *   const fe = useFieldErrors();
 *   <Field error={fe.errors.email} onBlur={() => fe.check('email', checkEmail(form.email))} … />
 *   on change: fe.clear('email')
 *   on submit: if (!fe.validate({ email: checkEmail(form.email), … })) return;
 *   on a failed request: setError(fe.fromServer(err))   // '' when the error went on a field
 */
export default function useFieldErrors() {
  const [errors, setErrors] = useState({});
  const clear = useCallback((k) => setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e)), []);
  const check = useCallback((k, msg) => setErrors((e) => ({ ...e, [k]: msg || undefined })), []);
  /** Set every check at once; true when all passed. */
  const validate = useCallback((checks) => {
    const failed = Object.fromEntries(Object.entries(checks).filter(([, m]) => m));
    setErrors(failed);
    return Object.keys(failed).length === 0;
  }, []);
  const fromServer = useCallback((err) => {
    const fields = err?.data?.fields;
    if (fields && Object.keys(fields).length) { setErrors(fields); return ''; }
    return err?.message || 'Something went wrong. Please try again.';
  }, []);
  return { errors, setErrors, clear, check, validate, fromServer };
}
